/**
 * Tiny module-level mirror of the scheduler's "last fired step"
 * marker. Lets independent subsystems (today: note recording)
 * ask "what step is playing right now?" without owning the
 * scheduler themselves.
 *
 * The sequencer's `PlaybackProvider` calls `markStep` on every
 * onStep fire with the step boundary's AudioContext time + the
 * step duration in seconds. Consumers call `stepPositionAt(t)`
 * with a later AudioContext time to compute the fractional step
 * position at `t`. If the scheduler hasn't fired yet (playback
 * stopped), the read returns null.
 *
 * Non-reactive by design — consumers read on-demand from event
 * handlers, never during render. If a React-friendly observable
 * is needed later, we can wrap this with a `useSyncExternalStore`
 * layer. For now the consumer (useNoteRecording) polls only when
 * a MIDI event fires, so per-frame re-renders are unnecessary.
 */

interface StepMark {
  /** AudioContext.currentTime when the step boundary fired. */
  when: number
  /** 0-based step index within the pattern. */
  stepIndex: number
  /** Current step duration in seconds. */
  stepDurSec: number
  /** Steps per pattern loop — used to wrap future positions. */
  stepCount: number
}

let lastMark: StepMark | null = null

export function markStep(params: StepMark): void {
  lastMark = params
}

export function clearStepMark(): void {
  lastMark = null
}

export function getStepMark(): StepMark | null {
  return lastMark
}

/**
 * Given an AudioContext time (typically `ctx.currentTime` at the
 * moment an incoming MIDI note arrived), return the step index +
 * fractional offset that corresponds to that time. Wraps across
 * pattern boundaries using the last-known `stepCount`.
 *
 * Returns null when no step mark exists (playback hasn't started
 * yet, or the pattern reset). Callers should treat that as "don't
 * record."
 */
export function stepPositionAt(
  time: number,
): { stepIndex: number; microOffset: number } | null {
  if (!lastMark) return null
  const { when, stepIndex, stepDurSec, stepCount } = lastMark
  if (stepDurSec <= 0 || stepCount <= 0) return null
  const deltaSteps = (time - when) / stepDurSec
  const absStep = stepIndex + deltaSteps
  const wrapped = ((absStep % stepCount) + stepCount) % stepCount
  const idx = Math.floor(wrapped)
  const microOffset = wrapped - idx
  return { stepIndex: idx, microOffset }
}
